import { useNavigate } from "react-router-dom";
import { AdminButton } from "@/components/admin";

interface SoundDetailActionsProps {
  elderlyName: string;
  elderlyId: number | null;
  deviceId: number | null;
  deviceName: string;
  statusLabel: string;
  checkNeeded: boolean;
}

const SoundDetailActions = ({
  elderlyName,
  elderlyId,
  deviceId,
  deviceName,
  statusLabel,
  checkNeeded,
}: SoundDetailActionsProps) => {
  const navigate = useNavigate();
  const buttons = [
    ...(elderlyId == null ? [] : [{ label: "생활 신호 보기", icon: "ri-heart-pulse-line", path: `/admin/users/seniors/${elderlyId}?tab=care` }]),
    ...(deviceId == null ? [] : [{ label: "기기 상태 보기", icon: "ri-router-line", path: "/admin/devices" }]),
    ...(elderlyId == null ? [] : [{ label: "어르신 정보 보기", icon: "ri-team-line", path: "/admin/users" }]),
  ];

  return (
    <div className="flex flex-col gap-4">
      {checkNeeded && (
        <div className="bg-orange-50 rounded-xl border border-orange-200 shadow-sm p-4">
          <div className="flex items-start gap-2.5">
            <i className="ri-error-warning-line text-sm text-orange-500 mt-0.5" />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-orange-700 mb-1">교차 확인 권장</h3>
              <p className="text-xs text-orange-600 leading-relaxed">
                이 소리 분석 결과는 자동 판정값입니다. 필요하면 생활 신호, 기기 상태, 긴급 이벤트 화면에서 같은 시간대의 상태를 함께 확인합니다.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-headphone-line text-sm text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">원음 검수</h3>
        </div>
        <p className="mb-3 text-xs leading-relaxed text-slate-500">일반 이벤트 화면에서는 원음 존재 여부를 추측하거나 검수 상태를 임의 변경하지 않습니다. 실제 수집된 원음은 전용 검수함에서 조회하고 판정합니다.</p>
        <AdminButton variant="plain" size="auto" onClick={() => navigate("/admin/sound-raw-audio-reviews")} className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-xs font-semibold text-violet-700 hover:bg-violet-100"><i className="ri-headphone-line" />원음 검수함 보기</AdminButton>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-links-line text-sm text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">관련 화면</h3>
        </div>
        <div className="flex flex-col gap-1.5">
          {buttons.map((button) => (
            <AdminButton variant="plain" size="auto"
              key={button.label}
              type="button"
              onClick={() =>
                navigate(button.path, {
                  state: {
                    fromSoundDetail: true,
                    elderName: elderlyName,
                    elderId: elderlyId,
                    selectedDevice: deviceName,
                    selectedDeviceId: deviceId,
                  },
                })
              }
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-50 hover:text-blue-600 transition-colors border border-transparent hover:border-slate-200 text-left"
            >
              <i className={`${button.icon} text-xs`} />
              {button.label}
              <i className="ri-arrow-right-s-line text-xs text-slate-400 ml-auto" />
            </AdminButton>
          ))}
          {elderlyId != null && <AdminButton variant="plain" size="auto"
            onClick={() => navigate("/admin/emergency", { state: { fromSoundDetail: true, elderName: elderlyName, elderId: elderlyId } })}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-colors text-left ${
              checkNeeded
                ? "bg-orange-50 text-orange-700 border border-orange-200 hover:bg-orange-100"
                : "text-slate-600 border border-transparent hover:bg-slate-50 hover:text-blue-600 hover:border-slate-200"
            }`}
          >
            <i className="ri-alarm-warning-line text-xs" />
            긴급 이벤트 보기
            <i className="ri-arrow-right-s-line text-xs text-slate-400 ml-auto" />
          </AdminButton>}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="flex items-center gap-2 mb-3">
          <i className="ri-information-line text-sm text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-700">관리 안내</h3>
        </div>
        <p className="text-xs text-slate-500 leading-relaxed">
          현재 상태는 {statusLabel}입니다. 위험 소리와 같은 시간대의 긴급 이벤트가 있는지는 긴급 이벤트 화면에서 별도로 확인합니다.
        </p>
      </div>
    </div>
  );
};

export default SoundDetailActions;
